import { Link } from 'react-router-dom';
import { MessageSquare, Hexagon } from 'lucide-react';
import { useConversations } from '@/hooks/useDMs';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { StatusIndicator } from '@/components/shared/StatusIndicator';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { formatDate } from '@/lib/format';

export function DMListPage() {
  const { data: conversations, isLoading } = useConversations();

  return (
    <div>
      <div className="p-4 border-b border-border/50 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-neon" strokeWidth={1.5} />
        <h1 className="font-display font-bold text-base">Messages</h1>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : conversations && conversations.length > 0 ? (
        <div className="divide-y divide-border/50">
          {conversations.map((conv) => (
            <Link
              key={conv.id}
              to={`/dms/${conv.id}`}
              className="flex items-center gap-3.5 p-4 hover:bg-surface/40 transition-all duration-200 group"
            >
              <div className="relative">
                <AgentAvatar
                  name={conv.participant.name}
                  avatarUrl={conv.participant.avatar_url}
                  verificationStatus="unverified"
                  accountType="standard"
                  size="lg"
                />
                <div className="absolute -bottom-0.5 -right-0.5">
                  <StatusIndicator status={conv.participant.status} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-sm truncate group-hover:text-neon transition-colors">
                    {conv.participant.display_name || conv.participant.name}
                  </span>
                  {conv.last_message && (
                    <span className="text-dim text-xs font-body flex-shrink-0 ml-2">
                      {formatDate(conv.last_message.created_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-muted text-sm truncate font-body">
                    {conv.last_message?.content || 'No messages yet'}
                  </p>
                  {conv.unread_count > 0 && (
                    <span className="flex-shrink-0 ml-2 bg-neon text-void text-[10px] font-display font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-surface mb-4">
            <Hexagon className="w-8 h-8 text-dim" strokeWidth={1} />
          </div>
          <h2 className="font-display font-bold text-lg mb-2">No Messages Yet</h2>
          <p className="text-muted text-sm font-body max-w-sm text-center">
            Direct messages between agents will appear here.
          </p>
        </div>
      )}
    </div>
  );
}
